/**
 * The calculator's scenario presets, and which engine offers which.
 *
 * THE SCENARIOS ARE THE APP'S OWN: the first-run scenarios of
 * kizuna-ai-lab/sokuji (`src/lib/setup/scenarios.ts`), named on this page by
 * the `dashboard.wallet.pricing.scenario.<id>.*` keys that
 * `scenario-copy.test.ts` holds to the app's wording. What a preset sets is the
 * app's too (which sides are translated, and whether they are read aloud); the
 * speaking shares are this calculator's own assumption, since the app has no
 * such number.
 *
 * EACH ENGINE HAS ITS OWN LIST. Qwen runs one session per direction and has no
 * face-to-face mode, so its list stops before the two face-to-face presets; an
 * engine can later gain a scenario of its own without touching the other's.
 */
import type { CalcSettings, Engine } from './cost-model';

export type CalculatorScenarioId =
  | 'understand-others'
  | 'be-heard'
  | 'subtitle-myself'
  | 'two-way-voice'
  | 'two-way-text'
  | 'face-to-face-voice'
  | 'face-to-face-text';

export type ScenarioChoice = CalculatorScenarioId | 'custom';

export interface CalculatorScenario {
  id: CalculatorScenarioId;
  settings: CalcSettings;
  /** Both directions over one stream, as face to face runs: one connection. */
  sharedStream: boolean;
}

// A side that is off still carries a share: the value its slider comes back
// with when the visitor turns that side on.
const IDLE_SHARE = 30;

export const CALCULATOR_SCENARIOS: readonly CalculatorScenario[] = [
  { id: 'understand-others', sharedStream: false, settings: { them: 'text', me: 'off', themShare: 60, meShare: IDLE_SHARE } },
  { id: 'be-heard', sharedStream: false, settings: { them: 'off', me: 'voice', themShare: IDLE_SHARE, meShare: 40 } },
  { id: 'subtitle-myself', sharedStream: false, settings: { them: 'off', me: 'text', themShare: IDLE_SHARE, meShare: 80 } },
  { id: 'two-way-voice', sharedStream: false, settings: { them: 'voice', me: 'voice', themShare: 30, meShare: 30 } },
  { id: 'two-way-text', sharedStream: false, settings: { them: 'text', me: 'text', themShare: 30, meShare: 30 } },
  { id: 'face-to-face-voice', sharedStream: true, settings: { them: 'voice', me: 'voice', themShare: 30, meShare: 30 } },
  { id: 'face-to-face-text', sharedStream: true, settings: { them: 'text', me: 'text', themShare: 30, meShare: 30 } },
];

export const ENGINE_SCENARIOS: Record<Engine, readonly CalculatorScenarioId[]> = {
  soniox: CALCULATOR_SCENARIOS.map((s) => s.id),
  qwen: ['understand-others', 'be-heard', 'subtitle-myself', 'two-way-voice', 'two-way-text'],
};

/** The wizard's first scenario, so the card opens on the case the app presents first. */
export const DEFAULT_SCENARIO: CalculatorScenarioId = 'understand-others';

export function scenarioById(id: CalculatorScenarioId): CalculatorScenario {
  const found = CALCULATOR_SCENARIOS.find((s) => s.id === id);
  if (!found) throw new Error(`unknown calculator scenario: ${id}`);
  return found;
}

/** Same outputs, and the same share for every side that is not off. */
export function sameSettings(a: CalcSettings, b: CalcSettings): boolean {
  return (
    a.them === b.them &&
    a.me === b.me &&
    (a.them === 'off' || a.themShare === b.themShare) &&
    (a.me === 'off' || a.meShare === b.meShare)
  );
}

/** The engine's first listed preset whose settings equal `s`, or null. */
export function matchingScenario(engine: Engine, s: CalcSettings): CalculatorScenarioId | null {
  return ENGINE_SCENARIOS[engine].find((id) => sameSettings(scenarioById(id).settings, s)) ?? null;
}
